import React, { useState, useRef, useEffect } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import i18next from 'i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import AlertAlgo from '@controls/AlertSnack';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import { saveEventReport, getInfoReport } from '@actions/reportEvent/index';
import * as StatusCode from '@scripts/StatusCode';
import { DateTimePicker, DatePicker } from '@progress/kendo-react-dateinputs';
import { Popup } from "@progress/kendo-react-popup";
import { IntlProvider, LocalizationProvider } from "@progress/kendo-react-intl";
import { loadLocalization } from '@utils/kendoLocalization';
import moment from "moment";
import { GetLanguage } from '@utils/language';
import { formatPostalCode } from '@utils';

loadLocalization();
moment.locale("fr");

const useStyles = makeStyles((theme) => ({
    appBar: {
        position: 'relative',
        backgroundColor: 'var(--color1) !important',
        color: 'var(--color2) !important'
    },
    title: {
        marginLeft: theme.spacing(2),
        flex: 1,
    },
}));

const fetchReport = (punchId, reportId) => {
    return new Promise((resolve) => {
        resolve(getInfoReport(punchId, reportId));
    });
};

export default function ReportEventForm(props) {
    const classes = useStyles();

    const { punchId, reportEventId, onClose, isEnabled } = props;

    const isReadOnly = !isEnabled;

    const alertRef = useRef(null);
    const popupContainerRef = useRef(null);
    const [locale, setLocale] = useState(GetLanguage());
    const [model, setModel] = React.useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [reportId, setReportId] = useState(null); // null = new report, otherwise editing

    // Height (px) of the app's bottom navigation bar (Accueil / Documents / Poinçons...).
    const BOTTOM_NAV_HEIGHT = 57;
    // Approximate height (px) reserved at the end of the form so the last
    // rows are not hidden behind the fixed save bar.
    const SAVE_BAR_HEIGHT = 64;

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setModel((prev) => ({ ...prev, [field]: value }));
    };

    const POPUP_HEIGHT = 400;
    const NAV_HEIGHT = 90; // measure your actual nav

    const renderDateTimeInput = (field) => (
        <DateTimePicker
            value={model[field] ? moment(model[field]).toDate() : null}
            onChange={(e) =>
                setModel((prev) => ({
                    ...prev,
                    [field]: e.value != null ? moment(e.value).format('YYYY-MM-DDTHH:mm:ss') : ''
                }))
            }
            disabled={isReadOnly}
            format="yyyy-MM-dd HH:mm"
            width="100%"
            popup={props => {
                const rect = props.anchor?.getBoundingClientRect();
                const spaceBelow = rect
                    ? window.innerHeight - rect.bottom - NAV_HEIGHT
                    : 0;
                const openUp = spaceBelow < POPUP_HEIGHT;
                return (
                    <Popup
                        {...props}
                        appendTo={popupContainerRef.current}
                        anchorAlign={{ horizontal: "left", vertical: openUp ? "top" : "bottom" }}
                        popupAlign={{ horizontal: "left", vertical: openUp ? "bottom" : "top" }}
                        collision={{ horizontal: "fit", vertical: "fit" }}
                    />
                );
            }}
        />
    );

    const renderDateInput = (field) => (
        <DatePicker
            value={model[field] ? moment(model[field]).toDate() : null}
            onChange={(e) =>
                setModel((prev) => ({
                    ...prev,
                    [field]: e.value != null
                        ? moment(e.value).format('YYYY-MM-DDT00:00:00')
                        : ''
                }))
            }
            disabled={isReadOnly}
            format="yyyy-MM-dd"
            width="100%"
            popup={props => <Popup {...props} appendTo={popupContainerRef.current} />}
        />
    );

    const renderInput = (field, type = 'text') => (
        <TextField
            fullWidth
            type={type}
            value={model[field] ?? ''}
            onChange={handleChange(field)}
            disabled={isReadOnly}
            InputLabelProps={type !== 'text' ? { shrink: true } : undefined}
        />
    );

    const renderCheckbox = (field) => (
        <Checkbox
            checked={model[field] === true}
            disabled={isReadOnly}
            onChange={(e) =>
                setModel((prev) => ({ ...prev, [field]: e.target.checked }))
            }
            name={field}
        />
    );

    useEffect(() => {
        if (!isLoading) {
            resizePage();
        }
    }, [isLoading]);

    // --- Load existing report from the "database" on mount / when punchId changes ---
    useEffect(() => {
        let cancelled = false;

        const loadReport = async () => {
            setIsLoading(true);
            try {
                const data = await fetchReport(punchId, reportEventId);
                if (data.status !== StatusCode.STATUS_OK) {
                    cancelled = true;
                }

                if (cancelled) {
                    return;
                }

                if (data.result != null) {
                    const report = data.result;

                    setReportId(report.webReportEventId);
                    setModel({
                        eventNature: report.eventNature ?? '',
                        eventNumber: report.eventNumber ?? '',
                        civicNumber: report.civicNumber ?? '',
                        streetName: report.streetName ?? '',
                        placeName: report.placeName ?? '',
                        office: report.office ?? '',
                        municipality: report.municipality ?? '',
                        postalCode: formatPostalCode(report.postalCode ?? ''),
                        placeType: report.placeType ?? '',
                        occurredDescription: report.occurredDescription ?? '',
                        occurredBetweenDescription: report.occurredBetweenDescription ?? '',
                        occurredAndDescription: report.occurredAndDescription ?? '',
                        occurredDateString: report.occurredDate ?? '',
                        occurredBetweenDateString: report.occurredBetweenDate ?? '',
                        occurredAndDateString: report.occurredAndDate ?? '',
                        reportedDescription: report.reportedDescription ?? '',
                        reportedDateString: report.reportedDate ?? '',
                        statusA: report.statusA ?? '',
                        companyNameA: report.companyNameA ?? '',
                        firstNameA: report.firstNameA ?? '',
                        birthDateAgeA: report.birthDateAgeA ?? '',
                        occupationA: report.occupationA ?? '',
                        homePhoneA: report.homePhoneA ?? '',
                        workPhoneA: report.workPhoneA ?? '',
                        genderA: report.genderA ?? '',
                        raceA: report.raceA ?? '',
                        heightA: report.heightA ?? '',
                        weightA: report.weightA ?? '',
                        hairA: report.hairA ?? '',
                        eyesA: report.eyesA ?? '',
                        languageA: report.languageA ?? '',
                        othersA: report.othersA ?? '',
                        remarksPartiesInvolvedA: report.remarksPartiesInvolvedA ?? '',
                        statusB: report.statusB ?? '',
                        companyNameB: report.companyNameB ?? '',
                        firstNameB: report.firstNameB ?? '',
                        birthDateAgeB: report.birthDateAgeB ?? '',
                        occupationB: report.occupationB ?? '',
                        homePhoneB: report.homePhoneB ?? '',
                        workPhoneB: report.workPhoneB ?? '',
                        genderB: report.genderB ?? '',
                        raceB: report.raceB ?? '',
                        heightB: report.heightB ?? '',
                        weightB: report.weightB ?? '',
                        hairB: report.hairB ?? '',
                        eyesB: report.eyesB ?? '',
                        languageB: report.languageB ?? '',
                        othersB: report.othersB ?? '',
                        remarksPartiesInvolvedB: report.remarksPartiesInvolvedB ?? '',
                        contactedResourceDescriptionA: report.contactedResourceDescriptionA ?? '',
                        vehicleA: report.vehicleA ?? '',
                        callTimeAString: report.callTimeA ?? '',
                        arrivalTimeAString: report.arrivalTimeA ?? '',
                        responderNamesA: report.responderNamesA ?? '',
                        contactedResourceDescriptionB: report.contactedResourceDescriptionB ?? '',
                        vehicleB: report.vehicleB ?? '',
                        callTimeBString: report.callTimeB ?? '',
                        arrivalTimeBString: report.arrivalTimeB ?? '',
                        responderNamesB: report.responderNamesB ?? '',
                        remarksResourceContacted: report.remarksResourceContacted ?? '',
                        narration: report.narration === 'O',
                        declaration: report.declaration === 'O',
                        remarks: report.remarks ?? '',
                        writtenBy: report.writtenBy ?? '',
                        accompaniedBy: report.accompaniedBy ?? '',
                        registerNumber: report.registerNumber ?? '',
                        signatureRegisterNumber: report.signatureRegisterNumber ?? '',
                        fileCopyGivenTo: report.fileCopyGivenTo ?? '',
                        signatureDateString: report.signatureDate ?? '',
                        signatureControlDateString: report.signatureControlDate ?? '',
                        fileCopyGivenDateString: report.fileCopyGivenDate ?? ''
                    });
                }
                // report == null => keep the empty defaults (new report mode)
            } catch (error) {
                console.error(error);
                showMessageError(i18next.t('error:generic-error'));
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadReport();

        // Ignore the result if the component unmounts (or punchId changes) mid-fetch
        return () => { cancelled = true; };
    }, [punchId]);

    const handlePostalCodeChange = (event) => {
        const formatted = formatPostalCode(event.target.value);
        setModel((prev) => ({ ...prev, postalCode: formatted }));
    };

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const resizePage = () => {
        setTimeout(() => {
            const header = document.querySelector('header')?.clientHeight ?? 0;
            const element = document.querySelector('#divForm');
            if (element != null) {
                element.style.height = `calc(100vh - ${header + BOTTOM_NAV_HEIGHT}px)`;
                element.style.overflowY = 'auto';
                element.style.overflowX = 'hidden';
            }
            // Prevent the page itself from scrolling — only #divForm scrolls
            document.body.style.overflow = 'hidden';
        }, 100);
    };

    const StyledFirstTableCell = withStyles((theme) => ({
        body: {
            fontSize: 14,
            width: '200px',
            textAlign: 'right !important',
            '@media (max-width: 600px)': {
                width: "150px"
            },
            fontWeight: "bold"
        },
    }))(TableCell);

    const isFormEmpty = () => {
        return Object.values(model).every(value =>
            value == null ||
            value === '' ||
            value === false ||
            (typeof value === 'string' && value.trim() === '')
        );
    };

    const handleSave = async () => {
        if (isSaving || isReadOnly) {
            return;
        }

        if (isFormEmpty()) {
            showMessageError(i18next.t('eventReport:validation.emptyForm'));
            return;
        }

        const payload = {
            reportId,
            punchId,
            eventNature: model.eventNature ?? '',
            eventNumber: model.eventNumber ?? '',
            civicNumber: model.civicNumber ?? '',
            streetName: model.streetName ?? '',
            placeName: model.placeName ?? '',
            office: model.office ?? '',
            municipality: model.municipality ?? '',
            postalCode: (model.postalCode ?? '').replace(/\s/g, ''),
            placeType: model.placeType ?? '',
            occurredDescription: model.occurredDescription ?? '',
            occurredBetweenDescription: model.occurredBetweenDescription ?? '',
            occurredAndDescription: model.occurredAndDescription ?? '',
            occurredDateString: model.occurredDateString ?? '',
            occurredBetweenDateString: model.occurredBetweenDateString ?? '',
            occurredAndDateString: model.occurredAndDateString ?? '',
            reportedDescription: model.reportedDescription ?? '',
            reportedDateString: model.reportedDateString ?? '',
            statusA: model.statusA ?? '',
            companyNameA: model.companyNameA ?? '',
            firstNameA: model.firstNameA ?? '',
            birthDateAgeA: model.birthDateAgeA ?? '',
            occupationA: model.occupationA ?? '',
            homePhoneA: model.homePhoneA ?? '',
            workPhoneA: model.workPhoneA ?? '',
            genderA: model.genderA ?? '',
            raceA: model.raceA ?? '',
            heightA: model.heightA ?? '',
            weightA: model.weightA ?? '',
            hairA: model.hairA ?? '',
            eyesA: model.eyesA ?? '',
            languageA: model.languageA ?? '',
            othersA: model.othersA ?? '',
            remarksPartiesInvolvedA: model.remarksPartiesInvolvedA ?? '',
            statusB: model.statusB ?? '',
            companyNameB: model.companyNameB ?? '',
            firstNameB: model.firstNameB ?? '',
            birthDateAgeB: model.birthDateAgeB ?? '',
            occupationB: model.occupationB ?? '',
            homePhoneB: model.homePhoneB ?? '',
            workPhoneB: model.workPhoneB ?? '',
            genderB: model.genderB ?? '',
            raceB: model.raceB ?? '',
            heightB: model.heightB ?? '',
            weightB: model.weightB ?? '',
            hairB: model.hairB ?? '',
            eyesB: model.eyesB ?? '',
            languageB: model.languageB ?? '',
            othersB: model.othersB ?? '',
            remarksPartiesInvolvedB: model.remarksPartiesInvolvedB ?? '',
            contactedResourceDescriptionA: model.contactedResourceDescriptionA ?? '',
            vehicleA: model.vehicleA ?? '',
            callTimeAString: model.callTimeAString ?? '',
            arrivalTimeAString: model.arrivalTimeAString ?? '',
            responderNamesA: model.responderNamesA ?? '',
            contactedResourceDescriptionB: model.contactedResourceDescriptionB ?? '',
            vehicleB: model.vehicleB ?? '',
            callTimeBString: model.callTimeBString ?? '',
            arrivalTimeBString: model.arrivalTimeBString ?? '',
            responderNamesB: model.responderNamesB ?? '',
            remarksResourceContacted: model.remarksResourceContacted ?? '',
            narration: model.narration === true ? 'O' : 'N',
            declaration: model.declaration === true ? 'O' : 'N',
            remarks: model.remarks ?? '',
            writtenBy: model.writtenBy ?? '',
            accompaniedBy: model.accompaniedBy ?? '',
            registerNumber: model.registerNumber ?? '',
            signatureRegisterNumber: model.signatureRegisterNumber ?? '',
            fileCopyGivenTo: model.fileCopyGivenTo ?? '',
            signatureDateString: model.signatureDateString ?? '',
            signatureControlDateString: model.signatureControlDateString ?? '',
            fileCopyGivenDateString: model.fileCopyGivenDateString ?? ''
        };

        //console.log('Saving report:', JSON.stringify(payload));

        setIsSaving(true);

        try {
            const result = await saveEventReport(payload);

            if (result.status === StatusCode.STATUS_OK) {
                //showMessageSuccess(i18next.t('common:successMessage'));
                onClose(true);
            } else {
                showMessageError(i18next.t('error:generic-error'));
            }
        } catch (error) {
            //console.error(error);
            showMessageError(i18next.t('error:generic-error'));
        } finally {
            // Re-enable the button (matters when the save failed and the form stays open)
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <AlertAlgo ref={alertRef} />
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <CircularProgress style={{ color: 'var(--color1)' }} />
                </div>
            </>
        );
    }

    return (
        <>
            <AlertAlgo ref={alertRef} />
            <div id="divForm" style={{ paddingBottom: `${SAVE_BAR_HEIGHT + 16}px` }}>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        {/* Popup container: inside the dialog, outside the PanelBar */}
                        <div ref={popupContainerRef} style={{ position: 'relative', zIndex: 11000 }} />
                        <PanelBar>
                            <PanelBarItem expanded={true} title={i18next.t('eventReport:section.section1')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Event */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.eventNature')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('eventNature')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.eventNumber')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('eventNumber')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section2')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Location */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.civicNumber')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('civicNumber')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.streetName')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('streetName')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.placeName')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('placeName')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.office')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('office')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.municipality')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('municipality')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.postalCode')}</StyledFirstTableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        value={model.postalCode ?? ''}
                                                        onChange={handlePostalCodeChange}
                                                        disabled={isReadOnly}
                                                        placeholder="A0A 0A0"
                                                        inputProps={{ maxLength: 7 }}
                                                    />
                                                    {/*{renderInput('postalCode')}*/}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.placeType')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('placeType')}</TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <StyledFirstTableCell>{`${i18next.t('eventReport:form.occurredDate')} (${i18next.t('eventReport:form.dateTime')})`}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('occurredDateString')}</TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <StyledFirstTableCell>{`${i18next.t('eventReport:form.occurredBetweenDate')} (${i18next.t('eventReport:form.dateTime')})`}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('occurredBetweenDateString')}</TableCell>
                                            </TableRow>

\                                            <TableRow>
                                                <StyledFirstTableCell>{`${i18next.t('eventReport:form.occurredAndDate')} (${i18next.t('eventReport:form.dateTime')})`}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('occurredAndDateString')}</TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <StyledFirstTableCell>{`${i18next.t('eventReport:form.reportedDate')} (${i18next.t('eventReport:form.dateTime')})`}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('reportedDateString')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section3')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Person A */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.statusA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('statusA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.companyNameA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('companyNameA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.firstNameA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('firstNameA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.birthDateAgeA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('birthDateAgeA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.occupationA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('occupationA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.homePhoneA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('homePhoneA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.workPhoneA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('workPhoneA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.genderA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('genderA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.raceA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('raceA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.heightA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('heightA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.weightA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('weightA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.hairA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('hairA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.eyesA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('eyesA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.languageA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('languageA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.othersA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('othersA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.remarks')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('remarksPartiesInvolvedA')}</TableCell>
                                            </TableRow>

                                            {/* Person B */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.statusB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('statusB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.companyNameB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('companyNameB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.firstNameB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('firstNameB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.birthDateAgeB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('birthDateAgeB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.occupationB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('occupationB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.homePhoneB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('homePhoneB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.workPhoneB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('workPhoneB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.genderB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('genderB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.raceB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('raceB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.heightB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('heightB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.weightB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('weightB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.hairB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('hairB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.eyesB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('eyesB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.languageB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('languageB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.othersB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('othersB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.remarks')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('remarksPartiesInvolvedB')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section4')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Contacted resource A */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.contactedResourceDescriptionA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('contactedResourceDescriptionA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.vehicleA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('vehicleA')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.callTimeA')}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('callTimeAString')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.arrivalTimeA')}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('arrivalTimeAString')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.responderNamesA')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('responderNamesA')}</TableCell>
                                            </TableRow>

                                            {/* Contacted resource B */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.contactedResourceDescriptionB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('contactedResourceDescriptionB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.vehicleB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('vehicleB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.callTimeB')}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('callTimeBString')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.arrivalTimeB')}</StyledFirstTableCell>
                                                <TableCell>{renderDateTimeInput('arrivalTimeBString')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.responderNamesB')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('responderNamesB')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.remarks')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('remarksResourceContacted')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section5')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Narrative */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.narration')}</StyledFirstTableCell>
                                                <TableCell>{renderCheckbox('narration')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.declaration')}</StyledFirstTableCell>
                                                <TableCell>{renderCheckbox('declaration')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.remarks')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('remarks')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section6')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            {/* Signature */}
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.writtenBy')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('writtenBy')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.accompaniedBy')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('accompaniedBy')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.registerNumber')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('registerNumber')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.signatureRegisterNumber')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('signatureRegisterNumber')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.signatureDate')}</StyledFirstTableCell>
                                                <TableCell>{renderDateInput('signatureDateString')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </PanelBarItem>

                            <PanelBarItem title={i18next.t('eventReport:section.section7')}>
                                <TableContainer component={Paper}>
                                    <Table className={classes.table} aria-label="spanning table">
                                        <TableBody>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.signatureControlDate')}</StyledFirstTableCell>
                                                <TableCell>{renderDateInput('signatureControlDateString')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.fileCopyGivenTo')}</StyledFirstTableCell>
                                                <TableCell>{renderInput('fileCopyGivenTo')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <StyledFirstTableCell>{i18next.t('eventReport:form.fileCopyGivenDate')}</StyledFirstTableCell>
                                                <TableCell>{renderDateInput('fileCopyGivenDateString')}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer >
                            </PanelBarItem>
                        </PanelBar >
                    </IntlProvider>
                </LocalizationProvider>
            </div>
            {isEnabled && <div
                style={{
                    position: 'fixed',
                    bottom: `${BOTTOM_NAV_HEIGHT}px`,
                    left: 0,
                    right: 0,
                    zIndex: 10000,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#fff',
                    boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.15)'
                }}
            >
                <Button
                    variant="contained"
                    disabled={isSaving}
                    style={
                        isSaving
                            ? {}
                            : { backgroundColor: 'var(--color1)', color: 'var(--color2)' }
                    }
                    startIcon={
                        isSaving
                            ? <CircularProgress size={18} color="inherit" />
                            : null
                    }
                    onClick={handleSave}
                >
                    {i18next.t('buttons:save')}
                </Button>
            </div>
            }
        </>
    )
}